import type { SupportDiagnosticEvent, SupportDiagnosticEventInput } from '@/types/supportDiagnostic';
import type { SQLiteOperationDiagnostic } from './SQLiteOperationDiagnostics';
import type { CircularLogEvent } from '@/utils/logBuffer';

type CapturedEvent = SupportDiagnosticEventInput & { occurredAt: string };

const occurrenceKey = (event: CapturedEvent): string | null => {
  // O prefixo identifica a emissão; horários dos dois buffers podem diferir alguns milissegundos.
  if (!/^\[\d{6,} \| \d{2}\/\d{2}\/\d{2} -- \d{2}:\d{2}:\d{2}:\d{3}\] \[[^\]\r\n]+\] /.test(event.message ?? '')) return null;
  return JSON.stringify([event.level, event.category, event.message]);
};

const compatibleData = (left: unknown, right: unknown): boolean => {
  if (left == null || right == null) return true;
  try { return JSON.stringify(left) === JSON.stringify(right); } catch { return false; }
};

export const mergeSyncDiagnosticEvents = (
  events: readonly CircularLogEvent[],
  essential: readonly SupportDiagnosticEvent[],
  sqliteOperations: readonly SQLiteOperationDiagnostic[] = [],
): CapturedEvent[] => {
  const recent: CapturedEvent[] = events.slice(-160).map(event => ({
    occurredAt: new Date(event.timestamp).toISOString(),
    level: event.level === 'custom' || event.level === 'log' ? 'info' : event.level,
    category: event.tag, name: 'buffered_log', message: event.message, data: event.data ?? null,
  }));
  const candidates = new Map<string, number[]>();
  recent.forEach((event, index) => {
    const key = occurrenceKey(event);
    if (key === null) return;
    const indexes = candidates.get(key) ?? [];
    indexes.push(index);
    candidates.set(key, indexes);
  });
  const copiedIndexes = new Set<number>();
  const preserved = essential.map(event => {
    const key = occurrenceKey(event);
    // Um resumo já compactado representa mais de uma ocorrência e continua inteiro.
    if (key === null || event.name !== 'sync_context' || event.repetitions !== 1) return event;
    const indexes = candidates.get(key);
    const position = indexes?.findIndex(index => compatibleData(recent[index].data, event.data)) ?? -1;
    if (!indexes || position < 0) return event;
    const [index] = indexes.splice(position, 1);
    copiedIndexes.add(index);
    // Mantém o contexto essencial e qualquer payload disponível apenas na cópia geral.
    return { ...event, data: event.data ?? recent[index].data };
  });
  const sqliteEvents: CapturedEvent[] = sqliteOperations.slice(-8).map(operation => ({
    occurredAt: new Date().toISOString(), level: 'error', category: 'sqlite',
    name: 'sqlite_operation_failure', data: operation,
  }));
  return [...sqliteEvents, ...recent.filter((_event, index) => !copiedIndexes.has(index)), ...preserved]
    .sort((a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt));
};
